import React, { useEffect, useMemo, useState } from 'react';
import { LogOut, WifiOff } from 'lucide-react';

/**
 * Wochen-Aushang für ein Tablet im Laden (Rolle "display").
 * Zeigt nur Arbeitsschichten der laufenden Woche (Mo–So). Abwesenheiten
 * (Urlaub/Krank) werden bewusst NICHT angezeigt – das Gerät ist öffentlich sichtbar.
 */

const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const DAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const CLOSED_WEEKDAY = 1; // Dienstag (Index in Mo..So) ist Ruhetag

const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

const mondayOf = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (d.getDay() + 6) % 7; // Mo=0 … So=6
  d.setDate(d.getDate() - offset);
  return d;
};

const isoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const isWorkShift = (s) => !s.type || s.type === 'WORK';

/** "Fleur Wolfersdorff" → "Fleur W." */
const shortName = (full) => {
  const parts = String(full || '').trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || '–';
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

// Gleichbleibende, gut lesbare Farbe pro Name (dunkles Theme)
const PALETTE = ['#00FF9C', '#D8B4FE', '#FF7F6C', '#7DD3FC', '#FCD34D', '#F9A8D4', '#86EFAC', '#FDBA74'];
const colorFor = (name) => {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

/** Läuft die Schicht gerade? (auch über Mitternacht) */
const isRunning = (shift, now) => {
  const start = minutesOf(shift.startTime);
  const end = minutesOf(shift.endTime);
  if (start === null || end === null) return false;
  const today = toDateStr(now);
  const yesterday = toDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const overnight = end <= start;
  if (shift.date === today) return overnight ? nowMin >= start : nowMin >= start && nowMin < end;
  if (shift.date === yesterday && overnight) return nowMin < end;
  return false;
};

const ShiftBoard = ({ shifts, logoUrl, onLogout, lastSync, offline }) => {
  const [now, setNow] = useState(() => new Date());

  // Uhr / Tageswechsel
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Bildschirm wach halten (falls der Browser es unterstützt)
  useEffect(() => {
    let lock = null;
    const request = async () => {
      try {
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          lock = await navigator.wakeLock.request('screen');
        }
      } catch (e) { /* nicht unterstützt oder verweigert */ }
    };
    request();
    document.addEventListener('visibilitychange', request);
    return () => {
      document.removeEventListener('visibilitychange', request);
      try { if (lock) lock.release(); } catch (e) { /* egal */ }
    };
  }, []);

  // Neue App-Version alle 15 Minuten prüfen (Aushang wird nie bedient)
  useEffect(() => {
    const MAIN_JS = /\/static\/js\/main\.[a-f0-9]+\.js/;
    const current = Array.from(document.scripts).map(sc => sc.src).find(src => MAIN_JS.test(src));
    if (!current) return undefined;
    const id = setInterval(async () => {
      try {
        const html = await (await fetch('/index.html', { cache: 'no-store' })).text();
        const latest = html.match(MAIN_JS);
        if (latest && !current.endsWith(latest[0])) window.location.reload();
      } catch (e) { /* offline */ }
    }, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const monday = mondayOf(now);
  const todayStr = toDateStr(now);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    const dateStr = toDateStr(d);
    const dayShifts = shifts
      .filter(s => s.date === dateStr && isWorkShift(s))
      .sort((a, b) => (minutesOf(a.startTime) ?? 0) - (minutesOf(b.startTime) ?? 0)
        || String(a.employeeName).localeCompare(String(b.employeeName)));
    return { index: i, date: d, dateStr, shifts: dayShifts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [shifts, monday.getTime()]);

  const running = shifts.filter(s => isWorkShift(s) && isRunning(s, now))
    .sort((a, b) => (minutesOf(a.endTime) ?? 0) - (minutesOf(b.endTime) ?? 0));
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const upcoming = (days.find(d => d.dateStr === todayStr)?.shifts || [])
    .filter(s => (minutesOf(s.startTime) ?? 0) > nowMin)
    .slice(0, 4);

  // Wie viele ausführliche Karten passen in eine Tagesspalte? Sonst kompakt darstellen.
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const columnSpace = Math.max(120, viewportHeight - 260); // Höhe der Tagesspalten ohne Kopf/Leisten
  const maxFullCards = Math.max(1, Math.floor(columnSpace / 92));
  const maxCompactCards = Math.max(2, Math.floor(columnSpace / 56));

  const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const sunday = days[6].date;
  const rangeStr = `${pad(monday.getDate())}.${pad(monday.getMonth() + 1)}. – ${pad(sunday.getDate())}.${pad(sunday.getMonth() + 1)}.${sunday.getFullYear()}`;

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col overflow-hidden select-none" style={{ cursor: 'none' }}>
      {/* Kopfzeile */}
      <header className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-zinc-800">
        <div className="flex items-center gap-4 min-w-0">
          {logoUrl && <img src={logoUrl} alt="" className="h-12 w-12 object-contain" />}
          <div className="min-w-0">
            <h1 className="text-3xl font-bold tracking-tight">Schichtplan <span className="text-accent-mint">KW {isoWeek(now)}</span></h1>
            <p className="text-zinc-400 text-lg">{rangeStr}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-bold tabular-nums leading-none">{timeStr}</div>
          <div className="text-zinc-400 text-lg mt-1">{DAY_NAMES[(now.getDay() + 6) % 7]}, {pad(now.getDate())}.{pad(now.getMonth() + 1)}.</div>
        </div>
      </header>

      {/* Jetzt / Als Nächstes */}
      <section className="flex items-stretch gap-4 px-6 py-3 border-b border-zinc-800 text-xl">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <span className="text-zinc-400 uppercase text-sm font-semibold tracking-wider">Jetzt im Dienst</span>
          {running.length === 0 ? (
            <span className="text-zinc-500">niemand</span>
          ) : running.map((s, i) => (
            <span key={`${s.employeeId}-${s.date}-${i}`} className="flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-700">
              <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: colorFor(s.employeeName) }} />
              <span className="font-semibold">{shortName(s.employeeName)}</span>
              <span className="text-zinc-400 text-base">bis {s.endTime}</span>
            </span>
          ))}
        </div>
        {upcoming.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap ml-auto">
            <span className="text-zinc-400 uppercase text-sm font-semibold tracking-wider">Gleich</span>
            {upcoming.map((s, i) => (
              <span key={`${s.employeeId}-up-${i}`} className="text-zinc-200"><span className="font-semibold">{s.startTime}</span> {shortName(s.employeeName)}</span>
            ))}
          </div>
        )}
      </section>

      {/* Woche */}
      <main className="flex-1 grid grid-cols-7 gap-2 p-3 min-h-0">
        {days.map(day => {
          const isToday = day.dateStr === todayStr;
          const isPast = day.dateStr < todayStr;
          const closed = day.index === CLOSED_WEEKDAY && day.shifts.length === 0;
          const compact = day.shifts.length > maxFullCards;
          const overflow = compact && day.shifts.length > maxCompactCards ? day.shifts.length - (maxCompactCards - 1) : 0;
          const visibleShifts = overflow ? day.shifts.slice(0, maxCompactCards - 1) : day.shifts;
          return (
            <div
              key={day.dateStr}
              className={`flex flex-col min-h-0 rounded-xl border ${isToday ? 'border-accent-mint bg-zinc-900' : 'border-zinc-800 bg-zinc-950'} ${isPast ? 'opacity-40' : ''}`}
            >
              <div className={`px-3 py-2 rounded-t-xl ${isToday ? 'bg-accent-mint text-black' : 'text-zinc-300'}`}>
                <div className="text-xl font-bold leading-tight">{DAY_SHORT[day.index]} <span className="font-normal">{pad(day.date.getDate())}.{pad(day.date.getMonth() + 1)}.</span></div>
                {isToday && <div className="text-xs font-semibold uppercase tracking-wider">Heute</div>}
              </div>
              <div className="flex-1 min-h-0 overflow-hidden px-2 py-2 space-y-2">
                {closed && <div className="text-center text-zinc-500 text-lg pt-6">Ruhetag</div>}
                {!closed && day.shifts.length === 0 && <div className="text-center text-zinc-600 text-base pt-6">–</div>}
                {visibleShifts.map((s, i) => {
                  const live = isToday && isRunning(s, now);
                  if (compact) {
                    return (
                      <div
                        key={`${s.employeeId}-${i}`}
                        className={`rounded-md px-2 py-1 bg-zinc-900 border-l-4 ${live ? 'ring-2 ring-accent-mint' : ''}`}
                        style={{ borderLeftColor: colorFor(s.employeeName) }}
                      >
                        <div className="text-base font-bold leading-tight truncate">{shortName(s.employeeName)}</div>
                        <div className="text-sm tabular-nums text-zinc-300 leading-tight whitespace-nowrap">{s.startTime}–{s.endTime}</div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`${s.employeeId}-${i}`}
                      className={`rounded-lg px-2.5 py-2 bg-zinc-900 border-l-4 ${live ? 'ring-2 ring-accent-mint' : ''}`}
                      style={{ borderLeftColor: colorFor(s.employeeName) }}
                    >
                      <div className="text-base xl:text-lg font-bold leading-tight truncate">{shortName(s.employeeName)}</div>
                      <div className="text-sm xl:text-base tabular-nums text-zinc-300 whitespace-nowrap">{s.startTime}–{s.endTime}</div>
                      {s.shiftType && <div className="text-sm text-zinc-400 truncate">{s.shiftType}</div>}
                      {s.notes && <div className="text-xs text-zinc-500 italic line-clamp-2">{s.notes}</div>}
                    </div>
                  );
                })}
                {overflow > 0 && <div className="text-center text-zinc-400 text-sm font-semibold">+{overflow} weitere</div>}
              </div>
            </div>
          );
        })}
      </main>

      {/* Fußzeile */}
      <footer className="flex items-center justify-between px-6 py-1.5 text-xs text-zinc-600">
        <span className="flex items-center gap-2">
          {offline ? (
            <span className="flex items-center gap-1.5 text-accent-coral"><WifiOff className="w-3.5 h-3.5" /> Offline – zeigt den letzten Stand</span>
          ) : (
            <span>Live{lastSync ? ` · aktualisiert ${pad(lastSync.getHours())}:${pad(lastSync.getMinutes())}` : ''}</span>
          )}
        </span>
        <button type="button" onClick={onLogout} className="flex items-center gap-1 text-zinc-700 hover:text-zinc-400 px-2 py-1" style={{ cursor: 'pointer' }} aria-label="Abmelden">
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </footer>
    </div>
  );
};

export default ShiftBoard;
